// context/AppContext.js - COM WEBSOCKET E SPOTS INTEGRADOS
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { initGlobalAudio } from '../utils/audioManager';
import { useSpotQueue } from '../hooks/useSpotQueue';


const AppContext = createContext();

function usuarioPersistivel(usuario = {}) {
  const {
    senha,
    senhaConfirmacao,
    signupInviteToken,
    inviteToken,
    ...seguro
  } = usuario || {};

  return seguro;
}

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const { slug } = useParams();

  const [tela, setTela] = useState(() => {
    const savedUser = localStorage.getItem('radio-ai-user');
    if (savedUser) {
      const user = JSON.parse(savedUser);
      if (user?.nome && user?.cep) {
        return 'cadastro';
      }
    }
    return 'cadastro';
  });
  
  const [loading, setLoading] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [erro, setErro] = useState('');
  
  const [usuario, setUsuario] = useState(() => {
    const savedUser = localStorage.getItem('radio-ai-user');
    return savedUser ? JSON.parse(savedUser) : {
      nome: '',
      negocio: '',
      cep: '',
      telefone: '',
      valores: 'qualidade, confiança, bom atendimento',
      publicoAlvo: '',
      ritmoAmbiente: '',
      quantidadeLocutores: '4',
    };
  });

  const [spot, setSpot] = useState(() => {
    const saved = sessionStorage.getItem('radio-ai-spot');
    return saved ? JSON.parse(saved) : {
      tipo: 'comercial',
      texto: '',
      preco: '',
      voz: 'masculina',
      textoMelhorado: ''
    };
  });

  const [audioGerado, setAudioGerado] = useState(() => {
    const saved = sessionStorage.getItem('radio-ai-audio');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    if (!audioGerado) {
      const saved = sessionStorage.getItem('radio-ai-audio');
      if (saved) setAudioGerado(JSON.parse(saved));
    }
  }, [audioGerado]);

  const [eventos, setEventos] = useState([]);
  const [novoEvento, setNovoEvento] = useState({
    dataInicio: '',
    dataFim: '',
    hora: '',
    repetirMinutos: ''
  });

  // ========================================
  // ESTADOS DO PLAYER
  // ========================================
  const [playlist, setPlaylist] = useState(() => {
    const saved = localStorage.getItem('radio-playlist');
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.filter(m => 
        m.url && (
          m.url.startsWith('http') ||
          m.url.startsWith('/uploads')
        )
      );
    }
    return [];
  });
  
  const [musicaAtual, setMusicaAtual] = useState(() => {
    const saved = localStorage.getItem('radio-musica-atual');
    return saved ? JSON.parse(saved) : null;
  });

  const [tocando, setTocando] = useState(false);
  const [spotTocando, setSpotTocando] = useState(null);
  const [volumeAtual, setVolumeAtual] = useState(1);
  
  // Estados para progresso
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // Monte os listeners do hook e exponha o que o AppContext/UI realmente usa
  const {
    spotQueue,
    addToQueue,
    removeFromQueue,
    clearQueue,
    isPlayingSpot
  } = useSpotQueue();


  // ========================================
  // CARREGAR CLIENTE PELO SLUG DA URL
  // ========================================
  useEffect(() => {
    if (!slug || slug === 'default') return;
    fetch(`/api/clientes/slug/${slug}`)
      .then(r => {
        if (!r.ok) throw new Error('Slug não encontrado');
        return r.json();
      })
      .then(({ cliente }) => {
        // O lookup por slug é público e não pode virar sessão local.
        if (cliente?.slug && cliente.slug !== slug) {
          setTela('cadastro');
        }
      })
      .catch(() => {
        setTela('cadastro');
      });
  }, [slug]);

  // ========================================
  // SINCRONIZAR COM PLAYER GLOBAL
  // ========================================
  useEffect(() => {
    const { audioElement } = initGlobalAudio();
    
    // Sincronizar estado inicial
    setTocando(!audioElement.paused);
    setVolumeAtual(audioElement.volume);

    // Handlers
    const handleTimeUpdate = () => {
      setCurrentTime(audioElement.currentTime);
      setDuration(audioElement.duration || 0);
    };

    const handleLoadedMetadata = () => {
      setDuration(audioElement.duration);
    };

    const handlePlay = () => setTocando(true);
    const handlePause = () => setTocando(false);

    // Adicionar listeners
    audioElement.addEventListener('timeupdate', handleTimeUpdate);
    audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    audioElement.addEventListener('play', handlePlay);
    audioElement.addEventListener('pause', handlePause);

    // Cleanup
    return () => {
      audioElement.removeEventListener('timeupdate', handleTimeUpdate);
      audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audioElement.removeEventListener('play', handlePlay);
      audioElement.removeEventListener('pause', handlePause);
    };
  }, []);

  // ========================================
  // FUNÇÕES AUXILIARES
  // ========================================
  const salvarUsuario = (campo, valor) => {
    setUsuario(prev => ({...prev, [campo]: valor}));
  };

  const salvarSpot = (campo, valor) => {
    setSpot(prev => ({...prev, [campo]: valor}));
  };

  const salvarEvento = (campo, valor) => {
    setNovoEvento(prev => ({...prev, [campo]: valor}));
  };

  // ========================================
  // PERSISTÊNCIA
  // ========================================
  useEffect(() => {
    // Salva sempre que tiver nome ou id — não exige CEP
    if (usuario.nome || usuario.id) {
      localStorage.setItem('radio-ai-user', JSON.stringify(usuarioPersistivel(usuario)));
    }
  }, [usuario]);

  useEffect(() => {
    if (spot.textoMelhorado) {
      sessionStorage.setItem('radio-ai-spot', JSON.stringify(spot));
    }
  }, [spot]);

  useEffect(() => {
    localStorage.setItem('radio-playlist', JSON.stringify(playlist));
  }, [playlist]);

  useEffect(() => {
    if (musicaAtual) {
      localStorage.setItem('radio-musica-atual', JSON.stringify(musicaAtual));
    }
  }, [musicaAtual]);

  useEffect(() => {
    if (audioGerado) {
      sessionStorage.setItem('radio-ai-audio', JSON.stringify(audioGerado));
    }
  }, [audioGerado]);

  // ========================================
  // VALUE COM TODOS OS ESTADOS
  // ========================================
  const value = {
    // Estados existentes
    tela,
    setTela,
    loading,
    setLoading,
    loadingAudio,
    setLoadingAudio,
    erro,
    setErro,
    usuario,
    setUsuario,
    spot,
    setSpot,
    audioGerado,
    setAudioGerado,
    eventos,
    setEventos,
    novoEvento,
    setNovoEvento,

    // Estados do Player
    playlist,
    setPlaylist,
    musicaAtual,
    setMusicaAtual,
    tocando,
    setTocando,
    spotTocando,
    setSpotTocando,
    volumeAtual,
    setVolumeAtual,
    
    // Estados de progresso
    currentTime,
    setCurrentTime,
    duration,
    setDuration,

    // ✅ Spots (NOVO!)
    spotQueue,
    addToQueue,
    removeFromQueue,
    clearQueue,
    isPlayingSpot,
    
    // Funções
    salvarUsuario,
    salvarSpot,
    salvarEvento,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
